/**
 * The console frame: dark sidebar, sticky topbar, scrolling content.
 * Mirrors the sidebar/topbar of `EstateHub Admin.dc.html`.
 */
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Buildings,
  CardsThree,
  Gear,
  Plus,
  Receipt,
  SignOut,
  SquaresFour,
  Tray,
  UserPlus,
  type Icon,
} from '@phosphor-icons/react';
import { useAuth } from '../auth/AuthContext';
import { useOverview } from '../queries/platform';
import { initials } from '../lib/format';

interface NavEntry {
  to: string;
  label: string;
  icon: Icon;
  /** Rendered as a pill on the right of the row. */
  badge?: number;
  badgeTone?: 'warn' | 'accent';
  end?: boolean;
}

const PAGE_META: Record<string, { title: string; sub: string }> = {
  '/': { title: 'Platform Overview', sub: 'Health of the Estate Hub network' },
  '/registrations': { title: 'Registrations', sub: 'Agencies waiting to be reviewed' },
  '/plan-requests': {
    title: 'Plan requests',
    sub: 'Agencies asking to be switched back on',
  },
  '/agencies': { title: 'Agencies', sub: 'Every tenant on the platform' },
  '/onboard': { title: 'Onboard Agency', sub: 'Create a tenant workspace directly' },
  '/plans': { title: 'Subscription Plans', sub: 'Pricing, limits and packaging' },
  '/billing': { title: 'Payments & Invoices', sub: 'Collections across all tenants' },
  '/settings': { title: 'Platform Settings', sub: 'Defaults, security and admin team' },
};

function metaFor(pathname: string) {
  if (PAGE_META[pathname]) return PAGE_META[pathname];
  if (pathname.startsWith('/agencies/')) {
    return { title: 'Agency', sub: 'Tenant workspace' };
  }
  return PAGE_META['/'];
}

export default function Shell() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Powers the sidebar badges; a stale count is how a registration gets missed.
  const { data: overview } = useOverview();

  const meta = metaFor(pathname);
  const openRegistrations = overview?.registrations.open ?? 0;
  const pendingPlanRequests = overview?.agencies.pendingPlanRequests ?? 0;

  const groups: { label: string; items: NavEntry[] }[] = [
    { label: 'Monitor', items: [{ to: '/', label: 'Overview', icon: SquaresFour, end: true }] },
    {
      label: 'Tenants',
      items: [
        {
          to: '/registrations',
          label: 'Registrations',
          icon: Tray,
          badge: openRegistrations || undefined,
          badgeTone: 'warn',
        },
        {
          to: '/plan-requests',
          label: 'Plan requests',
          icon: Receipt,
          badge: pendingPlanRequests || undefined,
          badgeTone: 'warn',
        },
        {
          to: '/agencies',
          label: 'Agencies',
          icon: Buildings,
          badge: overview?.agencies.total || undefined,
          badgeTone: 'accent',
        },
        { to: '/onboard', label: 'Onboard', icon: UserPlus },
      ],
    },
    {
      label: 'Revenue',
      items: [
        { to: '/plans', label: 'Plans', icon: CardsThree },
        { to: '/billing', label: 'Payments', icon: Receipt },
      ],
    },
    { label: 'System', items: [{ to: '/settings', label: 'Settings', icon: Gear }] },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-mark">
            <Buildings size={20} weight="fill" />
          </div>
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--paper)',
                lineHeight: 1.1,
              }}
            >
              Estate Hub
            </p>
            <p
              style={{
                margin: '3px 0 0',
                fontSize: 10.5,
                color: 'var(--nav-label)',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              Admin Console
            </p>
          </div>
        </div>

        <nav className="sidebar-nav scroll">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="nav-group-label">{group.label}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon size={17} weight={isActive ? 'fill' : 'regular'} />
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {item.badge !== undefined && (
                          <span className={`nav-badge tone-${item.badgeTone ?? 'accent'}`}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-user-row">
            <div className="avatar">{initials(admin?.name)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                className="truncate"
                style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--nav-strong)' }}
              >
                {admin?.name}
              </p>
              <p
                style={{
                  margin: '1px 0 0',
                  fontSize: 10.5,
                  color: 'var(--nav-label)',
                  textTransform: 'capitalize',
                }}
              >
                {admin?.role === 'owner' ? 'Owner' : 'Admin'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--nav-label)',
                display: 'flex',
                padding: 2,
              }}
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div>
            <h1>{meta.title}</h1>
            <p>{meta.sub}</p>
          </div>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => navigate('/onboard')}>
            <Plus size={15} weight="bold" /> Onboard Agency
          </button>
        </header>

        <main className="content scroll">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
